import { useMemo } from "react";
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const TYPE_META = {
  SHIPPER_ADDRESS: { label: "Versender", emoji: "🏭", color: "#4f46e5" },
  SHIPPING_PARTNER_HUB: { label: "Versand-Hub", emoji: "🏢", color: "#818cf8" },
  DELIVERING_PARTNER_HUB: { label: "Ziel-Hub", emoji: "🏢", color: "#818cf8" },
  CONSIGNEE_ADDRESS: { label: "Empfänger", emoji: "🏠", color: "#10b981" },
};

const MARKER_ORDER = [
  "SHIPPER_ADDRESS",
  "SHIPPING_PARTNER_HUB",
  "DELIVERING_PARTNER_HUB",
  "CONSIGNEE_ADDRESS",
];

const makeIcon = (label, color) =>
  L.divIcon({
    className: "cb-map-pin",
    html: `
      <div style="
        position: relative;
        display: inline-flex;
        flex-direction: column;
        align-items: center;
        font-family: 'IBM Plex Mono', monospace;
      ">
        <div style="
          background: ${color};
          color: #fff;
          font-size: 9px;
          font-weight: 700;
          letter-spacing: 0.15em;
          text-transform: uppercase;
          padding: 4px 8px;
          white-space: nowrap;
          margin-bottom: 4px;
        ">${label}</div>
        <div style="
          width: 14px;
          height: 14px;
          background: ${color};
          border: 3px solid #fff;
          border-radius: 50%;
          box-shadow: 0 1px 4px rgba(0,0,0,0.35);
        "></div>
      </div>
    `,
    iconSize: [80, 40],
    iconAnchor: [40, 38],
  });

const FitBounds = ({ points }) => {
  const map = useMap();
  if (points.length >= 2) {
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [60, 60] });
  }
  return null;
};

export const TrackingMap = ({ markers }) => {
  const sorted = useMemo(() => {
    if (!markers || !markers.length) return [];
    const byType = Object.fromEntries(markers.map((m) => [m.type, m]));
    return MARKER_ORDER.filter((t) => byType[t]).map((t) => byType[t]);
  }, [markers]);

  const points = sorted
    .filter((m) => typeof m.latitude === "number" && typeof m.longitude === "number")
    .map((m) => [m.latitude, m.longitude]);

  if (points.length < 2) return null;

  return (
    <section
      className="bg-white"
      data-testid="tracking-map-section"
    >
      <div className="border-b border-slate-100 px-6 py-4 md:px-8">
        <div className="text-xs font-medium uppercase tracking-wider text-slate-500">
          Route · {points.length} Stationen
        </div>
        <div className="mt-1 text-base font-bold tracking-tight text-slate-900 md:text-lg">
          Versender → {points.length === 4 ? "Versand-Hub → Ziel-Hub → " : ""}Empfänger
        </div>
      </div>
      <div className="h-[420px] w-full overflow-hidden" data-testid="tracking-map">
        <MapContainer
          center={points[0]}
          zoom={6}
          scrollWheelZoom={false}
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Polyline
            positions={points}
            pathOptions={{
              color: "#6366f1",
              weight: 3,
              opacity: 0.85,
              dashArray: "6 8",
            }}
          />
          {sorted.map((m, i) => {
            const meta = TYPE_META[m.type] || { label: m.type, color: "#0a0a0a" };
            return (
              <Marker
                key={`${m.type}-${i}`}
                position={[m.latitude, m.longitude]}
                icon={makeIcon(meta.label, meta.color)}
              >
                <Popup>
                  <div style={{ fontFamily: "IBM Plex Sans, sans-serif" }}>
                    <strong>{meta.label}</strong>
                    <br />
                    <span style={{ fontSize: 11, color: "#666" }}>
                      {m.latitude.toFixed(4)}, {m.longitude.toFixed(4)}
                    </span>
                  </div>
                </Popup>
              </Marker>
            );
          })}
          <FitBounds points={points} />
        </MapContainer>
      </div>
    </section>
  );
};
